import { motion } from 'framer-motion';
import {
  ArrowRight,
  Award,
  Building2,
  CheckCircle,
  Code2,
  Copy,
  Download,
  Eye,
  GraduationCap,
  Heart,
  Key,
  Landmark,
  Mail,
  Play,
  QrCode,
  Send,
  Server,
  Shield,
  Star,
  User,
  Users,
  Wallet,
  Zap,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';

const Home = () => {
  const [selectedCredential, setSelectedCredential] = useState(0);
  const [isVerifying, setIsVerifying] = useState(false);

  const useCases = [
    {
      icon: Heart,
      industry: 'Healthcare',
      title: 'Digital Health Records',
      description:
        'Enable patients to control and share their medical records securely with healthcare providers while maintaining privacy.',
      benefits: [
        'Patient-controlled data',
        'Interoperable records',
        'Privacy compliance',
        'Reduced fraud',
      ],
    },
    {
      icon: Landmark,
      industry: 'Financial Services',
      title: 'KYC & Identity Verification',
      description:
        'Streamline customer onboarding with verifiable identity credentials that reduce fraud and compliance costs.',
      benefits: [
        'Faster onboarding',
        'Reduced fraud',
        'Regulatory compliance',
        'Cost savings',
      ],
    },
    {
      icon: GraduationCap,
      industry: 'Education',
      title: 'Digital Diplomas & Certificates',
      description:
        'Issue tamper-proof academic credentials that students own and can share instantly with employers.',
      benefits: [
        'Instant verification',
        'Fraud prevention',
        'Student ownership',
        'Global acceptance',
      ],
    },
    {
      icon: Building2,
      industry: 'Enterprise',
      title: 'Employee Credentials',
      description:
        'Manage workforce identity with verifiable employment credentials, certifications, and access permissions.',
      benefits: [
        'Secure access control',
        'Skills verification',
        'Compliance tracking',
        'Mobile workforce',
      ],
    },
  ];

  const demoCredentials = [
    {
      id: 'employment',
      type: 'Employment Verification',
      issuer: 'TechCorp Inc.',
      holder: 'John Doe',
      status: 'verified',
      issuedDate: '2024-01-15',
      expiryDate: '2024-12-31',
      claims: {
        position: 'Senior Software Engineer',
        department: 'Engineering',
        employeeId: 'EMP-2024-001',
        startDate: '2022-03-01',
      },
    },
    {
      id: 'education',
      type: 'Academic Diploma',
      issuer: 'University',
      holder: 'Jane Smith',
      status: 'verified',
      issuedDate: '2023-06-15',
      expiryDate: 'never',
      claims: {
        degree: 'Master of Science',
        field: 'Computer Science',
        graduationDate: '2023-06-15',
        gpa: '3.9/4.0',
      },
    },
  ];

  const ssiAdoption = {
    realWorldExamples: [
      {
        name: 'Government of British Columbia',
        sector: 'Government',
        implementation: 'Digital ID & Verifiable Credentials',
      },
      {
        name: 'European Commission',
        sector: 'Government',
        implementation: 'EU Digital Identity Wallet',
      },
      {
        name: 'Government of Estonia',
        sector: 'Government',
        implementation: 'e-Residency Program',
      },
      {
        name: 'Leading University',
        sector: 'Education',
        implementation: 'Digital Diplomas on Blockchain',
      },
      {
        name: 'Technology Company',
        sector: 'Technology',
        implementation: 'Employee Digital Credentials',
      },
      {
        name: 'Financial Institution',
        sector: 'Financial Services',
        implementation: 'Digital Identity Solutions',
      },
    ],
    globalStats: [
      { number: '1.2B+', label: 'People Need Digital ID' },
      { number: '195', label: 'Countries Exploring SSI' },
      { number: '€20B', label: 'EU Digital ID Investment' },
      { number: '85%', label: 'Fraud Reduction Potential' },
    ],
    industries: [
      {
        name: 'Healthcare',
        description: 'Secure patient credentials',
        icon: 'Heart',
      },
      {
        name: 'Education',
        description: 'Tamper-proof certificates',
        icon: 'GraduationCap',
      },
      {
        name: 'Finance',
        description: 'KYC & identity verification',
        icon: 'Landmark',
      },
      {
        name: 'Government',
        description: 'Digital citizenship services',
        icon: 'Shield',
      },
    ],
  };

  const testimonials = [
    {
      name: 'Dr. Kim Cameron',
      role: 'Chief Identity Architect, Technology Company',
      content:
        'Self-sovereign identity represents a fundamental shift in how we think about digital identity. It puts individuals back in control of their own data.',
      rating: 5,
    },
    {
      name: 'Kaliya Young',
      role: 'Internet Identity Workshop Co-Founder',
      content:
        'SSI is not just about technology - it&apos;s about human dignity and the right to control your own digital identity in the modern world.',
      rating: 5,
    },
    {
      name: 'Christopher Allen',
      role: 'Principal Architect, Blockchain Commons',
      content:
        'Decentralized identity systems will enable a new generation of applications that respect user privacy while maintaining security and trust.',
      rating: 5,
    },
  ];

  const homeStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Documint - Self-Sovereign Identity Platform',
    description:
      'Transform any credential into unforgeable digital certificates. Instant verification, fraud-proof, globally accessible with W3C Verifiable Credentials.',
    url: 'https://documint.id',
    mainEntity: {
      '@type': 'SoftwareApplication',
      name: 'Documint Platform',
      description:
        'Self-Sovereign Identity platform for creating tamper-proof digital credentials',
      applicationCategory: 'SecurityApplication',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
      },
    },
  };

  return (
    <div className="min-h-screen pt-20">
      <SEOHead
        title="Transform Credentials into Unforgeable Digital Certificates"
        description="Create fraud-proof digital credentials with Self-Sovereign Identity. Instant verification, cryptographic security, W3C standards. Used by universities, enterprises & governments worldwide."
        keywords="self-sovereign identity, verifiable credentials, digital certificates, fraud prevention, W3C standards, DID, blockchain identity, instant verification, tamper-proof credentials, OpenID4VC"
        url="https://documint.id"
        structuredData={homeStructuredData}
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 bg-surface-100">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-4xl lg:text-6xl font-bold mb-6 relative">
                <span className="bg-gradient-to-r from-white via-gray-100 to-white bg-clip-text text-transparent drop-shadow-lg">
                  The Future of
                </span>
                <br />
                <span className="bg-gradient-to-r from-neon-300 via-neon-400 to-primary-400 bg-clip-text text-transparent animate-pulse drop-shadow-2xl">
                  Digital Credentials
                </span>
                <div className="absolute -inset-2 bg-gradient-to-r from-neon-600/10 via-primary-600/10 to-neon-600/10 blur-xl -z-10 rounded-lg"></div>
              </h1>
              <p className="text-xl text-gray-300 mb-8 max-w-xl leading-relaxed">
                Transform any credential into an{' '}
                <strong className="text-white">
                  unforgeable digital certificate
                </strong>{' '}
                that verifies instantly, anywhere in the world. No more fake
                degrees, fraudulent licenses, or document forgery — just
                mathematical certainty.
              </p>

              <div className="grid grid-cols-3 gap-4 mb-8 max-w-lg">
                <div className="text-center">
                  <div className="text-2xl font-bold text-neon-300">&lt;1s</div>
                  <div className="text-xs text-gray-400">Verification</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary-300">
                    100%
                  </div>
                  <div className="text-xs text-gray-400">Fraud-Proof</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-white">Global</div>
                  <div className="text-xs text-gray-400">Access</div>
                </div>
              </div>

              <p className="text-lg text-gray-300 mb-8 max-w-xl leading-relaxed">
                Universities issue degrees, companies verify employees,
                governments validate licenses — all through one seamless
                platform that makes fraud impossible and trust automatic.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/pricing"
                  className="btn-primary inline-flex items-center"
                >
                  Try It Free
                  <ArrowRight size={20} className="ml-2" />
                </Link>
                <Link
                  to="/features"
                  className="btn-secondary inline-flex items-center"
                >
                  <Play size={20} className="mr-2" />
                  Watch Demo
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative flex items-center justify-center"
            >
              {/* Phone Mockup */}
              <div className="relative mx-auto max-w-sm">
                {/* Phone Frame */}
                <div className="relative bg-gray-900 rounded-3xl p-2">
                  {/* Screen */}
                  <div className="bg-surface-100 rounded-2xl overflow-hidden relative">
                    {/* Status Bar */}
                    <div className="flex justify-between items-center px-6 py-3 bg-surface-200/50">
                      <span className="text-white text-xs font-medium">
                        9:41
                      </span>
                      <div className="flex items-center space-x-1">
                        <div className="w-4 h-2 bg-white rounded-sm"></div>
                        <div className="w-1 h-1 bg-white rounded-full"></div>
                        <div className="w-6 h-3 border border-white rounded-sm">
                          <div className="w-4 h-1.5 bg-white rounded-xs m-0.5"></div>
                        </div>
                      </div>
                    </div>

                    {/* App Content */}
                    <div className="bg-gradient-to-br from-surface-200 to-surface-100 p-4 min-h-[400px]">
                      {/* Header with Logo */}
                      <div className="text-center mb-6">
                        <div className="mb-3">
                          <img
                            src="/assets/images/logo.png"
                            alt="Documint Logo"
                            className="w-3/5 h-auto object-contain p-2 mx-auto"
                          />
                        </div>
                        <h3 className="text-white font-bold text-base mb-1">
                          Documint Credential Wallet
                        </h3>
                        <p className="text-gray-400 text-xs mb-2">
                          Your Verifiable Credentials
                        </p>
                        <div className="text-xs text-gray-500 bg-surface-200/50 rounded-lg p-2 max-w-full overflow-hidden">
                          <span className="text-neon-400 font-medium">
                            Documint:
                          </span>
                          <br />
                          <span className="break-all">
                            did:algo:mainnet:awvwersdf123adc2342asdfa
                          </span>
                        </div>
                      </div>

                      {/* Credentials List */}
                      <div className="space-y-3">
                        {/* Credential Card 1 */}
                        <div className="glass rounded-lg p-3 border border-neon-300/20 bg-gradient-to-r from-neon-600/10 to-primary-600/10 relative overflow-hidden">
                          <div className="absolute inset-0 bg-gradient-to-r from-neon-300/5 to-primary-300/5 rounded-lg"></div>
                          <div className="relative z-10">
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center space-x-2">
                                <div className="w-6 h-6 bg-blue-500/20 rounded-md flex items-center justify-center">
                                  <GraduationCap className="w-3 h-3 text-blue-400" />
                                </div>
                                <span className="text-white font-medium text-xs">
                                  University Diploma
                                </span>
                              </div>
                              <span className="text-green-400 text-xs font-medium bg-green-400/20 px-2 py-0.5 rounded-full">
                                Verified
                              </span>
                            </div>
                            <p className="text-gray-400 text-xs">
                              Computer Science • University
                            </p>
                          </div>
                          <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-neon-300/10 to-transparent rounded-full -mr-8 -mt-8"></div>
                        </div>

                        {/* Credential Card 2 */}
                        <div className="glass rounded-lg p-3 border border-primary-300/20 bg-gradient-to-r from-primary-600/10 to-neon-600/10 relative overflow-hidden">
                          <div className="absolute inset-0 bg-gradient-to-r from-primary-300/5 to-neon-300/5 rounded-lg"></div>
                          <div className="relative z-10">
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center space-x-2">
                                <div className="w-6 h-6 bg-purple-500/20 rounded-md flex items-center justify-center">
                                  <Building2 className="w-3 h-3 text-purple-400" />
                                </div>
                                <span className="text-white font-medium text-xs">
                                  Employment ID
                                </span>
                              </div>
                              <span className="text-green-400 text-xs font-medium bg-green-400/20 px-2 py-0.5 rounded-full">
                                Verified
                              </span>
                            </div>
                            <p className="text-gray-400 text-xs">
                              Senior Engineer • TechCorp
                            </p>
                          </div>
                          <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-primary-300/10 to-transparent rounded-full -mr-8 -mt-8"></div>
                        </div>

                        {/* Credential Card 3 */}
                        <div className="glass rounded-lg p-3 border border-neon-300/20 bg-gradient-to-r from-neon-600/10 to-primary-600/10 relative overflow-hidden">
                          <div className="absolute inset-0 bg-gradient-to-r from-neon-300/5 to-primary-300/5 rounded-lg"></div>
                          <div className="relative z-10">
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center space-x-2">
                                <div className="w-6 h-6 bg-green-500/20 rounded-md flex items-center justify-center">
                                  <Award className="w-3 h-3 text-green-400" />
                                </div>
                                <span className="text-white font-medium text-xs">
                                  Professional License
                                </span>
                              </div>
                              <span className="text-green-400 text-xs font-medium bg-green-400/20 px-2 py-0.5 rounded-full">
                                Verified
                              </span>
                            </div>
                            <p className="text-gray-400 text-xs">
                              Certified Developer • State Board
                            </p>
                          </div>
                          <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-green-300/10 to-transparent rounded-full -mr-8 -mt-8"></div>
                        </div>
                      </div>

                      {/* Bottom Navigation */}
                      <div className="absolute bottom-0 left-0 right-0 bg-surface-200/80 backdrop-blur p-3">
                        <div className="flex justify-around">
                          <div className="flex flex-col items-center">
                            <Wallet className="w-4 h-4 text-neon-400 mb-1" />
                            <span className="text-xs text-neon-400 font-medium">
                              Wallet
                            </span>
                          </div>
                          <div className="flex flex-col items-center">
                            <QrCode className="w-4 h-4 text-gray-500 mb-1" />
                            <span className="text-xs text-gray-500">Scan</span>
                          </div>
                          <div className="flex flex-col items-center">
                            <User className="w-4 h-4 text-gray-500 mb-1" />
                            <span className="text-xs text-gray-500">
                              Profile
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Documint Creation, Issuance & Verification Section */}
      <section className="py-20 bg-gradient-to-br from-surface-100 via-surface-200/50 to-surface-100">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-6 relative">
              <span className="bg-gradient-to-r from-white via-gray-100 to-white bg-clip-text text-transparent drop-shadow-lg">
                Documint Ecosystem
              </span>
              <br />
              <span className="bg-gradient-to-r from-primary-300 via-neon-400 to-primary-400 bg-clip-text text-transparent">
                Create • Issue • Verify
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Experience the complete identity lifecycle — from{' '}
              <strong className="text-white">
                blockchain-anchored DID creation
              </strong>{' '}
              to
              <strong className="text-neon-300">
                {' '}
                tamper-proof credential issuance
              </strong>{' '}
              to{' '}
              <strong className="text-primary-300">
                instant global verification
              </strong>
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="space-y-8">
                <div>
                  <h3 className="text-2xl font-bold text-white mb-4 flex items-center">
                    <div className="w-3 h-8 bg-gradient-to-b from-primary-400 to-neon-500 rounded-full mr-4"></div>
                    What is DocumintID?
                  </h3>
                  <p className="text-lg text-gray-300 leading-relaxed mb-6">
                    <strong className="text-white">
                      DocumintID is your cryptographic fingerprint
                    </strong>{' '}
                    — a unique, blockchain-anchored Decentralized Identifier
                    (DID) that serves as the foundation for all your digital
                    credentials and identity interactions.
                  </p>
                  <p className="text-lg text-gray-300 leading-relaxed">
                    Unlike traditional usernames or emails, your Documint is{' '}
                    <strong className="text-neon-300">
                      mathematically yours forever
                    </strong>{' '}
                    — no corporation can revoke it, no government can control
                    it, no hacker can steal it.
                  </p>
                </div>

                {/* Three-Step Process */}
                <div className="bg-gradient-to-r from-primary-600/20 to-neon-600/20 rounded-2xl p-6 border border-primary-300/30">
                  <h4 className="text-xl font-bold text-primary-300 mb-6">
                    The Complete Identity Journey
                  </h4>

                  <div className="space-y-6">
                    {/* Step 1: Create */}
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Key className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h5 className="font-bold text-white mb-2 flex items-center">
                          1. Create DocumintID
                          <span className="ml-2 px-2 py-1 bg-blue-500/20 text-blue-300 rounded-full text-xs">
                            DID Generation
                          </span>
                        </h5>
                        <p className="text-gray-300 text-sm leading-relaxed">
                          Generate your unique blockchain-anchored identifier in
                          seconds. Your DID becomes your permanent digital
                          address, cryptographically secured and globally
                          resolvable across all networks.
                        </p>
                      </div>
                    </div>

                    {/* Step 2: Issue */}
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Award className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h5 className="font-bold text-white mb-2 flex items-center">
                          2. Issue Verifiable Credentials
                          <span className="ml-2 px-2 py-1 bg-green-500/20 text-green-300 rounded-full text-xs">
                            W3C Standard
                          </span>
                        </h5>
                        <p className="text-gray-300 text-sm leading-relaxed">
                          Trusted organizations cryptographically sign
                          credentials to your DocumintID. Each credential is
                          tamper-proof, instantly verifiable, and maintains
                          perfect privacy through selective disclosure.
                        </p>
                      </div>
                    </div>

                    {/* Step 3: Verify */}
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h5 className="font-bold text-white mb-2 flex items-center">
                          3. Instant Global Verification
                          <span className="ml-2 px-2 py-1 bg-purple-500/20 text-purple-300 rounded-full text-xs">
                            0.3 Seconds
                          </span>
                        </h5>
                        <p className="text-gray-300 text-sm leading-relaxed">
                          Anyone, anywhere can verify your credentials in
                          real-time using cryptographic proofs. No central
                          database, no phone calls, no waiting — just
                          mathematical certainty.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              {/* Interactive Identity Flow Visualization */}
              <div className="relative">
                <div className="absolute -inset-8 bg-gradient-to-r from-primary-600/20 via-neon-600/20 to-primary-600/20 rounded-3xl blur-2xl"></div>
                <div className="relative glass p-8 rounded-3xl border border-primary-300/30">
                  <div className="text-center mb-8">
                    <h3 className="text-2xl font-bold text-white mb-3">
                      Identity in Action
                    </h3>
                    <p className="text-primary-300 font-medium">
                      Live Documint Ecosystem
                    </p>
                  </div>

                  {/* Identity Flow Chain */}
                  <div className="space-y-6">
                    {/* Documint Creation */}
                    <div className="bg-gradient-to-r from-blue-600/30 to-cyan-600/30 rounded-2xl p-6 border border-blue-400/40">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                            <Key className="w-6 h-6 text-blue-400" />
                          </div>
                          <div>
                            <div className="text-white font-bold">
                              DocumintID Created
                            </div>
                            <div className="text-blue-300 text-sm">
                              Blockchain-Anchored DID
                            </div>
                          </div>
                        </div>
                        <div className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-xs font-bold">
                          ACTIVE
                        </div>
                      </div>
                      <div className="bg-surface-200/40 rounded-lg p-3 mb-3">
                        <div className="text-xs text-gray-400 mb-1">
                          Your Unique Identifier
                        </div>
                        <div className="font-mono text-xs text-blue-300 break-all">
                          did:algo:mainnet:7x9k2m5n8p1q4r7s
                        </div>
                      </div>
                      <div className="text-xs text-gray-300">
                        ✓ Cryptographically secured • ✓ Globally resolvable • ✓
                        Permanently yours
                      </div>
                    </div>

                    {/* Credential Issuance */}
                    <div className="bg-gradient-to-r from-green-600/30 to-emerald-600/30 rounded-2xl p-6 border border-green-400/40">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
                            <Award className="w-6 h-6 text-green-400" />
                          </div>
                          <div>
                            <div className="text-white font-bold">
                              Credential Issued
                            </div>
                            <div className="text-green-300 text-sm">
                              University → You
                            </div>
                          </div>
                        </div>
                        <div className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-xs font-bold">
                          VERIFIED
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div className="bg-surface-200/40 rounded-lg p-2">
                          <div className="text-xs text-gray-400">
                            Credential
                          </div>
                          <div className="text-white text-sm font-semibold">
                            Computer Science Degree
                          </div>
                        </div>
                        <div className="bg-surface-200/40 rounded-lg p-2">
                          <div className="text-xs text-gray-400">Signature</div>
                          <div className="text-green-300 text-sm font-mono">
                            0x4a8f2b9c...
                          </div>
                        </div>
                      </div>
                      <div className="text-xs text-gray-300">
                        ✓ Tamper-proof • ✓ Instantly verifiable • ✓
                        Privacy-preserving
                      </div>
                    </div>

                    {/* Verification Process */}
                    <div className="bg-gradient-to-r from-purple-600/30 to-pink-600/30 rounded-2xl p-6 border border-purple-400/40">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
                            <CheckCircle className="w-6 h-6 text-purple-400" />
                          </div>
                          <div>
                            <div className="text-white font-bold">
                              Verification Complete
                            </div>
                            <div className="text-purple-300 text-sm">
                              Company HR validates in &lt;1s
                            </div>
                          </div>
                        </div>
                        <div className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-xs font-bold">
                          VALID
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2 mb-3">
                        <div className="bg-surface-200/40 rounded-lg p-2 text-center">
                          <div className="text-purple-300 font-bold text-lg">
                            &lt;1s
                          </div>
                          <div className="text-xs text-gray-400">
                            Verification Time
                          </div>
                        </div>
                        <div className="bg-surface-200/40 rounded-lg p-2 text-center">
                          <div className="text-purple-300 font-bold text-lg">
                            100%
                          </div>
                          <div className="text-xs text-gray-400">Accuracy</div>
                        </div>
                        <div className="bg-surface-200/40 rounded-lg p-2 text-center">
                          <div className="text-purple-300 font-bold text-lg">
                            ∞
                          </div>
                          <div className="text-xs text-gray-400">
                            Global Reach
                          </div>
                        </div>
                      </div>
                      <div className="text-xs text-gray-300">
                        ✓ No central authority • ✓ Mathematical proof • ✓ Zero
                        fraud possible
                      </div>
                    </div>
                  </div>

                  {/* Connection Flow Lines */}
                  <div className="absolute top-28 left-1/2 w-px h-16 bg-gradient-to-b from-blue-400 to-green-400 transform -translate-x-1/2"></div>
                  <div className="absolute top-72 left-1/2 w-px h-16 bg-gradient-to-b from-green-400 to-purple-400 transform -translate-x-1/2"></div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Technical Advantages */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="grid md:grid-cols-4 gap-6"
          >
            <div className="glass p-6 rounded-xl border border-white/10 text-center">
              <div className="w-16 h-16 bg-blue-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-blue-400" />
              </div>
              <h4 className="font-bold text-white mb-2">Quantum-Resistant</h4>
              <p className="text-gray-400 text-sm">
                Future-proof cryptography protecting your identity forever
              </p>
            </div>

            <div className="glass p-6 rounded-xl border border-white/10 text-center">
              <div className="w-16 h-16 bg-green-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-green-400" />
              </div>
              <h4 className="font-bold text-white mb-2">Lightning Fast</h4>
              <p className="text-gray-400 text-sm">
                Sub-second verification across 50+ blockchain networks
              </p>
            </div>

            <div className="glass p-6 rounded-xl border border-white/10 text-center">
              <div className="w-16 h-16 bg-purple-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-purple-400" />
              </div>
              <h4 className="font-bold text-white mb-2">Globally Accepted</h4>
              <p className="text-gray-400 text-sm">
                Built for global compatibility and standards compliance
              </p>
            </div>

            <div className="glass p-6 rounded-xl border border-white/10 text-center">
              <div className="w-16 h-16 bg-orange-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Key className="w-8 h-8 text-orange-400" />
              </div>
              <h4 className="font-bold text-white mb-2">Privacy First</h4>
              <p className="text-gray-400 text-sm">
                Share only what you choose with selective disclosure
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How It Works - Revolutionary Credential Journey */}
      <section className="py-20 bg-gradient-to-br from-surface-100 via-surface-200/50 to-surface-100 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary-600/5 via-neon-600/5 to-primary-600/5"></div>
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-neon-600/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-primary-600/10 rounded-full blur-3xl"></div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-20"
          >
            <div className="inline-block mb-6">
              <div className="px-4 py-2 bg-gradient-to-r from-neon-600/20 to-primary-600/20 rounded-full border border-neon-300/30">
                <span className="text-neon-300 font-semibold text-sm">
                  DIGITAL TRANSFORMATION
                </span>
              </div>
            </div>
            <h2 className="text-4xl lg:text-6xl font-bold mb-6 relative">
              <span className="bg-gradient-to-r from-white via-gray-100 to-white bg-clip-text text-transparent drop-shadow-lg">
                How the Magic
              </span>
              <br />
              <span className="bg-gradient-to-r from-neon-300 via-neon-400 to-primary-400 bg-clip-text text-transparent animate-pulse">
                Happens
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Experience the seamless transformation from traditional paper
              certificates to
              <strong className="text-white">
                {' '}
                mathematically perfect digital credentials
              </strong>{' '}
              that work everywhere, instantly.
            </p>
          </motion.div>

          {/* Revolutionary Process Flow */}
          <div className="relative">
            {/* Central Timeline */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-neon-400 via-primary-400 to-neon-400 opacity-30 hidden lg:block"></div>

            <div className="space-y-20">
              {/* Step 1: Create Identity */}
              <motion.div
                initial={{ opacity: 0, x: -100 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="grid lg:grid-cols-2 gap-16 items-center"
              >
                <div className="lg:text-right">
                  <div className="inline-flex items-center space-x-3 mb-4">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-sm">1</span>
                    </div>
                    <span className="text-blue-300 font-semibold text-sm uppercase tracking-wider">
                      Identity Genesis
                    </span>
                  </div>
                  <h3 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                    Create Your Digital Soul
                  </h3>
                  <p className="text-lg text-gray-300 leading-relaxed mb-6">
                    Birth your{' '}
                    <strong className="text-blue-300">DocumintID</strong> — a
                    cryptographic fingerprint that&apos;s uniquely yours
                    forever. In seconds, you gain a blockchain-anchored identity
                    that no one can replicate, revoke, or control.
                  </p>
                  <div className="flex flex-wrap gap-3 lg:justify-end">
                    <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm">
                      Quantum-Resistant
                    </span>
                    <span className="px-3 py-1 bg-cyan-500/20 text-cyan-300 rounded-full text-sm">
                      Globally Unique
                    </span>
                    <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm">
                      Forever Yours
                    </span>
                  </div>
                </div>
                <div className="relative">
                  <div className="absolute -inset-4 bg-gradient-to-r from-blue-600/20 to-cyan-600/20 rounded-3xl blur-2xl"></div>
                  <div className="relative glass p-8 rounded-3xl border border-blue-300/30">
                    <div className="text-center">
                      <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-2xl">
                        <Key className="w-12 h-12 text-white" />
                      </div>
                      <div className="bg-surface-200/40 rounded-lg p-4 mb-4">
                        <div className="text-xs text-gray-400 mb-2">
                          Your DocumintID
                        </div>
                        <div className="font-mono text-sm text-blue-300 break-all">
                          did:algo:mainnet:genesis-7x9k2m
                        </div>
                      </div>
                      <div className="text-green-400 text-sm font-semibold flex items-center justify-center">
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Identity Created Successfully
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Step 2: Receive Credentials */}
              <motion.div
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="grid lg:grid-cols-2 gap-16 items-center"
              >
                <div className="lg:order-2">
                  <div className="inline-flex items-center space-x-3 mb-4">
                    <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-sm">2</span>
                    </div>
                    <span className="text-green-300 font-semibold text-sm uppercase tracking-wider">
                      Credential Alchemy
                    </span>
                  </div>
                  <h3 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                    Receive Unbreakable Proof
                  </h3>
                  <p className="text-lg text-gray-300 leading-relaxed mb-6">
                    Trusted institutions cryptographically sign credentials
                    directly to your DocumintID. Each certificate becomes{' '}
                    <strong className="text-emerald-300">
                      mathematically impossible to forge
                    </strong>{' '}
                    — more secure than any physical document ever created.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-sm">
                      Tamper-Proof
                    </span>
                    <span className="px-3 py-1 bg-emerald-500/20 text-emerald-300 rounded-full text-sm">
                      Instantly Issued
                    </span>
                    <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-sm">
                      Cryptographically Signed
                    </span>
                  </div>
                </div>
                <div className="relative lg:order-1">
                  <div className="absolute -inset-4 bg-gradient-to-r from-green-600/20 to-emerald-600/20 rounded-3xl blur-2xl"></div>
                  <div className="relative glass p-8 rounded-3xl border border-green-300/30">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                            <GraduationCap className="w-6 h-6 text-blue-400" />
                          </div>
                          <div>
                            <div className="text-white font-bold">
                              University
                            </div>
                            <div className="text-blue-300 text-sm">
                              Issuing Credential...
                            </div>
                          </div>
                        </div>
                        <div className="animate-pulse text-green-400">⚡</div>
                      </div>
                      <div className="bg-gradient-to-r from-green-600/30 to-emerald-600/30 rounded-2xl p-4 border border-green-400/40">
                        <div className="text-white font-bold mb-2">
                          Computer Science Degree
                        </div>
                        <div className="text-emerald-300 text-sm mb-2">
                          Bachelor of Science • Magna Cum Laude
                        </div>
                        <div className="bg-surface-200/40 rounded-lg p-2">
                          <div className="text-xs text-gray-400">
                            Cryptographic Signature
                          </div>
                          <div className="font-mono text-xs text-green-300">
                            0x7f3a9b8c2d4e6f1a...
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Step 3: Instant Verification */}
              <motion.div
                initial={{ opacity: 0, x: -100 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="grid lg:grid-cols-2 gap-16 items-center"
              >
                <div className="lg:text-right">
                  <div className="inline-flex items-center space-x-3 mb-4">
                    <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-sm">3</span>
                    </div>
                    <span className="text-purple-300 font-semibold text-sm uppercase tracking-wider">
                      Trust Lightning
                    </span>
                  </div>
                  <h3 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                    Verify in few seconds
                  </h3>
                  <p className="text-lg text-gray-300 leading-relaxed mb-6">
                    Share with anyone, anywhere. Verifiers get{' '}
                    <strong className="text-purple-300">
                      instant mathematical proof
                    </strong>
                    of authenticity. No phone calls, no background checks, no
                    waiting — just pure cryptographic certainty in milliseconds.
                  </p>
                  <div className="flex flex-wrap gap-3 lg:justify-end">
                    <span className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-sm">
                      &lt;1s Speed
                    </span>
                    <span className="px-3 py-1 bg-pink-500/20 text-pink-300 rounded-full text-sm">
                      100% Accurate
                    </span>
                    <span className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-sm">
                      Global Reach
                    </span>
                  </div>
                </div>
                <div className="relative">
                  <div className="absolute -inset-4 bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-3xl blur-2xl"></div>
                  <div className="relative glass p-8 rounded-3xl border border-purple-300/30">
                    <div className="text-center">
                      <div className="flex items-center justify-center space-x-4 mb-6">
                        <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                          <User className="w-8 h-8 text-white" />
                        </div>
                        <div className="text-2xl text-purple-300">→</div>
                        <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
                          <Building2 className="w-8 h-8 text-white" />
                        </div>
                      </div>
                      <div className="bg-gradient-to-r from-purple-600/30 to-pink-600/30 rounded-2xl p-4 border border-purple-400/40 mb-4">
                        <div className="text-white font-bold mb-2">
                          Company HR Department
                        </div>
                        <div className="text-purple-300 text-sm">
                          Verifying University Degree...
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="bg-surface-200/40 rounded-lg p-2 text-center">
                          <div className="text-green-400 font-bold">✓</div>
                          <div className="text-xs text-gray-400">Authentic</div>
                        </div>
                        <div className="bg-surface-200/40 rounded-lg p-2 text-center">
                          <div className="text-green-400 font-bold">&lt;1s</div>
                          <div className="text-xs text-gray-400">Verified</div>
                        </div>
                        <div className="bg-surface-200/40 rounded-lg p-2 text-center">
                          <div className="text-green-400 font-bold">100%</div>
                          <div className="text-xs text-gray-400">Trust</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Call to Action */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="text-center mt-20"
          >
            <div className="bg-gradient-to-r from-primary-600/20 to-neon-600/20 rounded-3xl p-8 border border-neon-300/30 max-w-4xl mx-auto">
              <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4">
                Ready to Experience the Future?
              </h3>
              <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
                Join thousands who&apos;ve already transformed their credentials
                from forgeable paper to unbreakable digital proof.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/pricing"
                  className="btn-primary inline-flex items-center text-lg px-8 py-4"
                >
                  Start Your Transformation
                  <ArrowRight size={24} className="ml-3" />
                </Link>
                <Link
                  to="/features"
                  className="btn-secondary inline-flex items-center text-lg px-8 py-4"
                >
                  <Play size={24} className="mr-3" />
                  Watch the Magic
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* App Download Section */}
      <section id="download" className="py-20 bg-surface-100">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                Get started today.
                <span className="text-neon-300">
                  {' '}
                  Download your Documint Wallet App today.
                </span>
              </h2>
              <p className="text-lg text-gray-300 mb-8 leading-relaxed">
                Go to App Store, install Documint wallet and start receiving
                your digital credentials today!
              </p>

              <div className="space-y-6 mb-8">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">
                      Easiest way to get started in digital credentials journey
                    </h3>
                    <p className="text-gray-300">
                      Simple setup process gets you ready in minutes
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <Wallet className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">
                      Your wallet and credentials and you control how you want
                    </h3>
                    <p className="text-gray-300">
                      Complete ownership and control over your digital identity
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <Users className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">
                      Share your credentials with 1000s of verifiers in your
                      region
                    </h3>
                    <p className="text-gray-300">
                      Wide network of trusted organizations and services
                    </p>
                  </div>
                </div>
              </div>

              {/* Phone Number Input */}
              <div className="glass rounded-xl p-6 mb-8 border border-white/20">
                <h3 className="text-lg font-semibold text-white mb-4">
                  Get the app link sent to your phone
                </h3>
                <div className="flex gap-4">
                  <input
                    type="tel"
                    placeholder="Your phone number..."
                    className="flex-1 px-4 py-3 bg-surface-200/40 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-neon-300 focus:border-neon-300"
                  />
                  <button className="btn-primary whitespace-nowrap">
                    Get started
                  </button>
                </div>
                <p className="text-sm text-gray-300 mt-3">
                  We&apos;ll send you a secure download link via SMS
                </p>
              </div>

              {/* App Store Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                {/* Apple App Store */}
                <button className="flex items-center justify-center bg-black text-white font-medium py-3 px-6 rounded-lg hover:bg-gray-800 transition-colors min-w-[180px]">
                  <div className="mr-3">
                    <svg
                      className="w-8 h-8"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <div className="text-xs opacity-80">Download on the</div>
                    <div className="text-lg font-semibold">App Store</div>
                  </div>
                </button>

                {/* Google Play Store */}
                <a
                  href="https://play.google.com/store/apps/details?id=id.documint.wallet"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center bg-black text-white font-medium py-3 px-6 rounded-lg hover:bg-gray-800 transition-colors min-w-[180px]"
                >
                  <div className="mr-3">
                    <svg
                      className="w-8 h-8"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <div className="text-xs opacity-80">GET IT ON</div>
                    <div className="text-lg font-semibold">Google Play</div>
                  </div>
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="relative">
                {/* Realistic Phone Mockup */}
                <div className="bg-gradient-to-b from-gray-800 to-gray-900 rounded-[3rem] p-2 max-w-xs mx-auto border border-gray-700">
                  {/* Phone Frame */}
                  <div className="bg-black rounded-[2.5rem] overflow-hidden relative">
                    {/* Status Bar */}
                    <div className="bg-black px-6 py-2 flex justify-between items-center text-white text-sm">
                      <span>9:41</span>
                      <div className="flex items-center space-x-1">
                        <div className="flex space-x-1">
                          <div className="w-1 h-1 bg-white rounded-full"></div>
                          <div className="w-1 h-1 bg-white rounded-full"></div>
                          <div className="w-1 h-1 bg-white rounded-full"></div>
                          <div className="w-1 h-1 bg-white/50 rounded-full"></div>
                        </div>
                        <div className="w-6 h-3 border border-white rounded-sm">
                          <div className="w-4 h-1.5 bg-green-500 rounded-sm m-0.5"></div>
                        </div>
                      </div>
                    </div>

                    {/* Dynamic Island/Notch */}
                    <div className="absolute top-2 left-1/2 transform -translate-x-1/2 w-20 h-6 bg-black rounded-full border-2 border-gray-800"></div>

                    {/* Screen Content */}
                    <div className="bg-gradient-to-br from-primary-600 via-primary-700 to-neon-600 px-6 py-8 text-center min-h-[500px] flex flex-col justify-center">
                      {/* App Icon */}
                      <div className="flex items-center justify-center mx-auto mb-6">
                        <img
                          src="/assets/images/logo.png"
                          alt="Documint Logo"
                          className="w-3/5 h-[102px] object-contain"
                        />
                      </div>

                      <h3 className="text-white font-bold text-xl mb-2">
                        Documint Wallet
                      </h3>
                      <p className="text-white/90 text-sm mb-8">
                        Your Digital Identity, Secured
                      </p>

                      {/* Credential Cards Preview */}
                      <div className="space-y-3 mb-8">
                        <div className="bg-white/15 backdrop-blur-md rounded-xl p-4 border border-white/20">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                              <GraduationCap className="w-4 h-4 text-white" />
                            </div>
                            <div className="text-left">
                              <div className="text-white text-sm font-medium">
                                University Degree
                              </div>
                              <div className="text-white/70 text-xs">
                                Verified Credential
                              </div>
                            </div>
                            <div className="ml-auto text-green-400 text-sm">
                              ✓
                            </div>
                          </div>
                        </div>

                        <div className="bg-white/15 backdrop-blur-md rounded-xl p-4 border border-white/20">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                              <Building2 className="w-4 h-4 text-white" />
                            </div>
                            <div className="text-left">
                              <div className="text-white text-sm font-medium">
                                Employee ID
                              </div>
                              <div className="text-white/70 text-xs">
                                Active Status
                              </div>
                            </div>
                            <div className="ml-auto text-green-400 text-sm">
                              ✓
                            </div>
                          </div>
                        </div>
                      </div>

                      <button className="w-full bg-white text-primary-600 font-semibold py-4 rounded-2xl shadow-lg transform hover:scale-105 transition-transform">
                        Get Started Now
                      </button>
                    </div>

                    {/* Home Indicator */}
                    <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 w-32 h-1 bg-white/30 rounded-full"></div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary-600 to-neon-600">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Start Building with Verifiable Credentials
            </h2>
            <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
              Ship production‑ready identity experiences with standards that
              work across ecosystems.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://app.passhub.documint.id/signup"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white text-primary-600 font-semibold py-3 px-8 rounded-lg hover:bg-gray-100 transition-colors duration-200"
              >
                Get API Key
              </a>
              <a
                href="https://app.passhub.documint.id/signup"
                target="_blank"
                rel="noopener noreferrer"
                className="border-2 border-white text-white font-semibold py-3 px-8 rounded-lg hover:bg-white hover:text-primary-600 transition-colors duration-200"
              >
                Explore Features
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
